import {
  useBulkUndoCheckInClassSessionMutation,
  usePatchInstructorCheckInTimeMutation,
} from "@/modules/attendance/presentation/api/queries";
import {
  EditCheckInTimeDialog,
  type EditCheckInTimeTarget,
} from "@/modules/attendance/presentation/ui/EditCheckInTimeDialog";
import { formatDateTime } from "@/shared/lib/date-time";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  confirm,
  toast,
} from "@devhop/ui";
import { Loader2, ClockIcon, PencilIcon, Undo2Icon, UserCheckIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { parseYearMonthFromDateYyyyMmDd } from "@/modules/reports/domain/lib/parse-year-month";
import { useInstructorPayrollBatchByMonthQuery } from "@/modules/reports/presentation/api/queries";
import type { ClassSessionDTO } from "../../domain/contracts/sessions";
import {
  useCorrectInstructorCheckInClassSession,
  useSessionInstructorAttendeesQuery,
} from "../api/queries";
import {
  CorrectInstructorCheckInDialog,
  type InstructorCheckInAttendee,
} from "./CorrectInstructorCheckInDialog";
import { SessionInstructorRoleBadges } from "./SessionInstructorRoleBadges";

type ClassSessionInstructorAttendanceCardProps = {
  session: ClassSessionDTO;
  canCorrect?: boolean;
  onCheckIn?: () => void;
  onCorrected?: () => void;
};

export function ClassSessionInstructorAttendanceCard({
  session,
  canCorrect = false,
  onCheckIn,
  onCorrected,
}: ClassSessionInstructorAttendanceCardProps) {
  const { data, isLoading } = useSessionInstructorAttendeesQuery(session.id);
  const correctMutation = useCorrectInstructorCheckInClassSession(session.id);
  const patchTimeMutation = usePatchInstructorCheckInTimeMutation({
    onSuccess: onCorrected,
  });
  const bulkUndoMutation = useBulkUndoCheckInClassSessionMutation();
  const attendees = data?.attendees ?? [];

  const yearMonth = useMemo(() => {
    try {
      return parseYearMonthFromDateYyyyMmDd(session.date);
    } catch {
      return null;
    }
  }, [session.date]);
  const payrollBatchQuery = useInstructorPayrollBatchByMonthQuery(
    yearMonth?.year ?? null,
    yearMonth?.month ?? null,
  );
  const payrollLocked = !!payrollBatchQuery.data;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTimeOpen, setEditTimeOpen] = useState(false);
  const [selectedAttendee, setSelectedAttendee] =
    useState<InstructorCheckInAttendee | null>(null);
  const [editTimeTarget, setEditTimeTarget] =
    useState<EditCheckInTimeTarget | null>(null);
  const [selectedUndoIds, setSelectedUndoIds] = useState<Set<string>>(
    () => new Set(),
  );

  const correctOptions = useMemo(() => {
    const checkedInIds = new Set(attendees.map((a) => a.instructorId));
    return session.instructorIds
      .map((id, index) => ({
        id,
        name: session.instructorNames[index] ?? id,
      }))
      .filter((inst) => !checkedInIds.has(inst.id));
  }, [attendees, session.instructorIds, session.instructorNames]);

  const canUndo =
    canCorrect &&
    !payrollLocked &&
    session.status !== "cancelled" &&
    attendees.length > 0;

  const canShowCorrect =
    canCorrect &&
    !payrollLocked &&
    session.status !== "cancelled" &&
    attendees.length > 0 &&
    correctOptions.length > 0;

  const canEditTime =
    canCorrect &&
    !payrollLocked &&
    session.status !== "cancelled" &&
    attendees.length > 0;

  const isSubmitting =
    correctMutation.isPending ||
    bulkUndoMutation.isPending ||
    patchTimeMutation.isPending;

  const toggleUndo = useCallback((id: string) => {
    setSelectedUndoIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleOpenCorrect = (attendee: InstructorCheckInAttendee) => {
    setSelectedAttendee(attendee);
    setDialogOpen(true);
  };

  const handleOpenEditTime = (attendee: InstructorCheckInAttendee) => {
    setEditTimeTarget({
      id: attendee.id,
      label: attendee.instructorName,
      checkedInAt: attendee.checkedInAt,
    });
    setEditTimeOpen(true);
  };

  const handleConfirmEditTime = async (values: {
    id: string;
    checkedInAt: string;
  }) => {
    await patchTimeMutation.mutateAsync({
      checkInId: values.id,
      checkedInAt: values.checkedInAt,
    });
    setEditTimeOpen(false);
    setEditTimeTarget(null);
  };

  const handleConfirm = async (values: {
    wrongCheckInId: string;
    correctInstructorId: string;
    reason: string;
  }) => {
    await correctMutation.mutateAsync(values);
    setDialogOpen(false);
    setSelectedAttendee(null);
    onCorrected?.();
  };

  const handleBulkUndo = async () => {
    const instructorCheckInIds = Array.from(selectedUndoIds);
    if (instructorCheckInIds.length === 0) return;

    const ok = await confirm({
      title: "ຍົກເລີກການກວດຊື່ຄູສອນ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກການກວດຊື່ຄູ ${instructorCheckInIds.length} ຄົນ?`,
      actionText: "ຍົກເລີກ",
      ActionProps: {
        variant: "destructive",
      },
    });
    if (!ok) return;

    try {
      const result = await bulkUndoMutation.mutateAsync({
        sessionId: session.id,
        attendanceIds: [],
        instructorCheckInIds,
      });

      const successCount = result.instructorSucceeded?.length ?? 0;
      const failCount = result.instructorFailed?.length ?? 0;

      if (successCount > 0) {
        setSelectedUndoIds(new Set());
        toast.success(`ຍົກເລີກການກວດຊື່ຄູສອນສຳເລັດ: ${successCount} ຄົນ`);
        onCorrected?.();
      }
      if (failCount > 0) {
        toast.error(`ຍົກເລີກການກວດຊື່ຄູສອນບໍ່ສຳເລັດ: ${failCount} ຄົນ`);
      }
    } catch {
      // Error shown by mutation onError
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <UserCheckIcon className="size-4" />
              ການກວດຊື່ຄູສອນ
            </CardTitle>
            <Badge variant="secondary">{attendees.length} ຄົນ</Badge>
          </div>
          {payrollLocked ? (
            <p className="text-amber-800 text-xs dark:text-amber-200">
              ປິດງວດ payroll ແລ້ວ — ບໍ່ສາມາດກວດຊື່/ແກ້ໄຂ/ການກວດຊື່ຄູໄດ້
            </p>
          ) : null}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
          ) : attendees.length === 0 ? (
            <div className="space-y-3">
              <p className="text-muted-foreground text-sm">
                ຍັງບໍ່ມີການກວດຊື່ຄູສອນ
              </p>
              {session.status === "completed" && onCheckIn && !payrollLocked && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={onCheckIn}
                >
                  <UserCheckIcon className="size-3.5" />
                  ກວດຊື່ຄູສອນ
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {canUndo && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground text-xs">
                    ເລືອກເພື່ອຍົກເລີກການກວດຊື່
                  </span>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={isSubmitting}
                      onClick={() => {
                        setSelectedUndoIds(
                          new Set(attendees.map((a) => a.id)),
                        );
                      }}
                    >
                      ເລືອກທັງໝົດ
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={isSubmitting || selectedUndoIds.size === 0}
                      onClick={() => setSelectedUndoIds(new Set())}
                    >
                      ລ້າງ
                    </Button>
                  </div>
                </div>
              )}

              <ul className="space-y-2">
                {attendees.map((attendee) => (
                  <li
                    key={attendee.id}
                    className="flex items-center justify-between gap-2 rounded-md border px-3 py-2"
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-2">
                      {canUndo && (
                        <Checkbox
                          id={`undo-instructor-card-${attendee.id}`}
                          className="mt-0.5 shrink-0"
                          checked={selectedUndoIds.has(attendee.id)}
                          disabled={isSubmitting}
                          onCheckedChange={() => toggleUndo(attendee.id)}
                        />
                      )}
                      <label
                        htmlFor={
                          canUndo
                            ? `undo-instructor-card-${attendee.id}`
                            : undefined
                        }
                        className={
                          canUndo ? "min-w-0 cursor-pointer" : "min-w-0"
                        }
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium text-sm">
                            {attendee.instructorName}
                          </p>
                          {/* role + substitute */}
                          <SessionInstructorRoleBadges
                            role={attendee.instructorRole}
                            isSubstitute={attendee.isSubstitute}
                          />
                        </div>
                        <p className="text-muted-foreground text-xs">
                          {formatDateTime(attendee.checkedInAt)}
                        </p>
                      </label>
                    </div>
                    {(canShowCorrect || canEditTime) && (
                      <div className="flex shrink-0 gap-1">
                        {canEditTime && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            disabled={isSubmitting}
                            onClick={() => handleOpenEditTime(attendee)}
                          >
                            <ClockIcon className="size-3.5" />
                            ເວລາ
                          </Button>
                        )}
                        {canShowCorrect && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            disabled={isSubmitting}
                            onClick={() => handleOpenCorrect(attendee)}
                          >
                            <PencilIcon className="size-3.5" />
                            ແກ້ໄຂ
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>

              {canUndo && selectedUndoIds.size > 0 && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground text-xs">
                    ເລືອກຍົກເລີກ: ຄູ {selectedUndoIds.size} ຄົນ
                  </span>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="gap-1.5"
                    onClick={handleBulkUndo}
                    disabled={isSubmitting}
                  >
                    {bulkUndoMutation.isPending ? (
                      <>
                        <Loader2 className="size-3.5 animate-spin" />
                        ກຳລັງຍົກເລີກ...
                      </>
                    ) : (
                      <>
                        <Undo2Icon className="size-3.5" />
                        ຍົກເລີກການກວດຊື່
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
          {session.status === "completed" &&
            canCorrect &&
            attendees.length > 0 &&
            correctOptions.length === 0 && (
              <p className="mt-3 text-muted-foreground text-xs">
                ຄູທີ່ assign ທັງໝົດກວດຊື່ແລ້ວ — ຖ້າຍັງຜິດ ກະລຸນາແກ້ໄຂການ assign ຄູກ່ອນ
              </p>
            )}
        </CardContent>
      </Card>

      <CorrectInstructorCheckInDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        wrongAttendee={selectedAttendee}
        correctOptions={correctOptions}
        onConfirm={handleConfirm}
        isSubmitting={correctMutation.isPending}
      />

      <EditCheckInTimeDialog
        open={editTimeOpen}
        onOpenChange={setEditTimeOpen}
        target={editTimeTarget}
        onConfirm={handleConfirmEditTime}
        isSubmitting={patchTimeMutation.isPending}
      />
    </>
  );
}
